// Devices tab: device list only; fleet map lives in GlobalMapDock (portal). Center-on-location
// updates MapDock mapQuery and opens the map panel.

import { useEffect, useMemo, useRef, type JSX } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import DeviceListPage from '../components/DeviceListView/panels/DeviceListPage';
import { useMapDock } from '../context/MapDockContext';

import type { DevicesHomePageProps } from './types/PageProps';

export type { DevicesHomePageProps };

export default function DevicesHomePage({
  operatorScopedEventId = null,
  operatorScopedEventName = null,
  operatorScopedRaceId = null,
  operatorScopedUnretrievedOnly = false,
}: DevicesHomePageProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const lastOpenedLatLngRef = useRef<string | null>(null);
  const {
    setMapDockOpen,
    setMapQuery,
    selectedDeviceIds,
    radiusFilterEnabled,
    radiusCenter,
    radiusKm,
    setRadiusFilterEnabled,
    setRadiusCenter,
    setBaseVisibleDeviceIds,
  } = useMapDock();

  useEffect(() => {
    if (location.pathname !== '/') return;
    const raw = location.search;
    if (!raw || raw === '?') return;
    const q = raw.startsWith('?') ? raw.slice(1) : raw;
    const sp = new URLSearchParams(q);
    if (sp.has('lat') && sp.has('lng')) {
      const latLngKey = `${sp.get('lat')},${sp.get('lng')}`;
      if (latLngKey === lastOpenedLatLngRef.current) return;
      lastOpenedLatLngRef.current = latLngKey;
      setMapQuery(sp.toString());
      setMapDockOpen(true);
    }
  }, [location.pathname, location.search, setMapDockOpen, setMapQuery]);

  const onCenterMapOnLocation = useMemo(
    () => (latVal: number, lngVal: number, deviceId?: string) => {
      const sp = new URLSearchParams();
      sp.set('lat', String(latVal));
      sp.set('lng', String(lngVal));
      if (deviceId) sp.set('deviceId', deviceId);
      const qs = sp.toString();
      setMapQuery(qs);
      setMapDockOpen(true);
      if (location.pathname === '/') {
        navigate({ pathname: '/', search: `?${qs}` }, { replace: true });
      }
    },
    [navigate, setMapDockOpen, setMapQuery, location.pathname],
  );

  return (
    <div className="h-[calc(100vh-56px-60px)] w-full overflow-hidden" style={{ background: 'var(--bg-base)' }}>
      <DeviceListPage
        onCenterMapOnLocation={onCenterMapOnLocation}
        operatorScopedEventId={operatorScopedEventId}
        operatorScopedEventName={operatorScopedEventName}
        operatorScopedRaceId={operatorScopedRaceId}
        operatorScopedUnretrievedOnly={operatorScopedUnretrievedOnly}
        mapSelectedDeviceIds={selectedDeviceIds}
        mapRadiusFilter={
          radiusFilterEnabled && radiusCenter
            ? {
              centerLat: radiusCenter.lat,
              centerLng: radiusCenter.lng,
              radiusKm,
            }
            : null
        }
        onClearMapRadiusFilter={() => {
          setRadiusFilterEnabled(false);
          setRadiusCenter(null);
        }}
        onVisibleIdsBeforeLocationFilterChange={setBaseVisibleDeviceIds}
      />
    </div>
  );
}
